import { useEffect, useRef, useState } from 'react';
import { isEmpty } from '../ui/utils';
import { useApi } from './useApi';

const formatTime = (date) => date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

/**
 * Polls the quote-level macavity ECS job (GET /quotes/{id}/job_status) and exposes a heuristic
 * progress signal plus completion detection. The same recompute gates both the CAT modeling
 * outputs and the Authority designation (Gate 2b loss-pick needs the cap_5m run; Gate 4 TIV
 * aggregation needs the cell registry), so any tab whose data depends on macavity can consume this
 * to show progress and react to completion.
 *
 * The percentage is heuristic, not a true fraction (PENDING -> up to 50, RUNNING -> up to 80,
 * STOPPED -> 100) — this mirrors the CAT tab's existing signal (see ProgressBar.jsx, which this hook
 * generalises; that component is left untouched and may adopt the hook later).
 *
 * @param {object}   opts
 * @param {string}   opts.quoteId      quote whose job to poll
 * @param {boolean} [opts.enabled]     poll only while true (e.g. the consuming tab is active)
 * @param {function}[opts.onComplete]  fired once per job when it transitions to STOPPED (receives the response)
 * @param {number}  [opts.intervalMs]  poll cadence (default 5000)
 * @returns {{ active: boolean, progress: number, statusText: string, startedAt: ?string }}
 *   active   — a job is in flight (found, not yet complete)
 *   progress — 0..100 heuristic
 */
export function useMacavityJobStatus({ quoteId, enabled = true, onComplete, intervalMs = 5000 }) {
  const { getMacavityStatus } = useApi();
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState('');
  const [startedAt, setStartedAt] = useState(null);
  const [active, setActive] = useState(false);

  // Hold the latest onComplete in a ref so a new callback identity each render does not re-arm the
  // interval (which would restart polling and lose in-flight progress).
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!enabled || !quoteId) return undefined;

    let cancelled = false;
    let ecsArnId = null;
    let started = false;
    let seenJob = false;
    let completedArn = null; // guards against firing onComplete twice for one job (status lingers STOPPED)

    const idle = () => {
      if (cancelled) return;
      setActive(false);
      setProgress(0);
      setStatusText('');
      setStartedAt(null);
      ecsArnId = null;
      started = false;
      seenJob = false;
    };

    const tick = async () => {
      try {
        const res = await getMacavityStatus(quoteId, ecsArnId || undefined);
        if (cancelled) return;

        if (isEmpty(res)) {
          // No job in flight. The snapshot fetch already reflects the latest state, so fall to idle.
          idle();
          return;
        }

        const arn = res.ecsArnId || ecsArnId;

        if (res.taskStatus === 'STOPPED') {
          if (arn && arn === completedArn) return; // already handled this job
          completedArn = arn;
          setActive(false);
          setProgress(100);
          setStatusText(`Complete – ${formatTime(new Date(res.systemTime))}`);
          if (onCompleteRef.current) onCompleteRef.current(res);
          // Brief "complete" flash, then back to idle so a later job is tracked fresh; polling continues.
          setTimeout(idle, 1500);
          return;
        }

        if (arn && arn === completedArn) return; // stale duplicate of a job we already completed

        if (!seenJob) {
          seenJob = true;
          setActive(true);
          setProgress(20); // initial jump when a job is first observed
        }
        if (!ecsArnId && res.ecsArnId) ecsArnId = res.ecsArnId;

        if (res.taskStatus === 'PENDING') {
          if (!started) {
            started = true;
            setStartedAt(`Started – ${formatTime(new Date(res.systemTime))}`);
          }
          setProgress((p) => Math.min(p + 2, 50));
        } else {
          setStatusText(`Running – ${formatTime(new Date(res.systemTime))}`);
          setProgress((p) => (p < 50 ? 50 : Math.min(p + 2, 80)));
        }
      } catch (e) {
        // Transient (network/401-retry handled in useApiService) — leave state as-is; next tick retries.
      }
    };

    tick();
    const id = setInterval(tick, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled, quoteId, getMacavityStatus, intervalMs]);

  return { active, progress, statusText, startedAt };
}
